import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AttachmentService } from 'src/sb-tools/typescript/services/attachment.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs/internal/Subject';
import { AttachmentBase } from 'src/sb-tools/typescript/models/attachment-base.model';
import { AttachmentType } from 'src/sb-tools/typescript/enums/attachment-type.enum';
import { DocumentViewerModalComponent } from 'src/sb-modules/Frontend-Document-Viewer-Module/components/document-viewer-modal/document-viewer-modal.component';
import { take } from 'rxjs/operators';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { PrintService } from 'src/sb-modules/Frontend-Document-Viewer-Module/services/print.service';
import { DOCUMENT_VIEWER_MODAL_ID } from 'src/sb-tools/typescript/constants/nested-modal-ids';
import { InboxAttachmentBase } from '@sb-tools/typescript/models/inbox-attachment-base.model';
import { DocumentWindowMessageType } from '@sb-document-viewer/enums/document-window-message-type';
import { NgClass } from '@angular/common';
import { InlineSVGComponent } from '../../../Frontend-Inline-SVG-Module/inline-svg.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'sb-file-preview',
    templateUrl: './file-preview.component.html',
    styleUrls: ['./file-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, NgClass, InlineSVGComponent, TooltipModule],
})
export class FilePreviewComponent implements OnInit, OnDestroy {
    @Input() attachment: AttachmentBase | InboxAttachmentBase;
    @Input() attachments?;
    @Input() canBeRemoved;
    @Input() canBeShared?;
    @Input() fileIndex;
    @Input() isFileNameWithTooltip?: boolean;
    @Input() isEnablePostMessages = false;
    @Input() isEnablePostMessageForWidget = false;
    @Input() isRemoveFromServer?: boolean = true;
    @Input() isPreventPreviewInModal?: boolean;
    @Input() permalink?: string;
    @Input() reply?;
    @Input() isOnlyPreviewIcon?: boolean = false;

    @Output() forwardFile: EventEmitter<any> = new EventEmitter();

    attachmentTypeEnum = AttachmentType;
    fileName;
    fileType: string;
    isImage = false;
    isPrintingAvailable;

    private unsubscriber = new Subject<void>();

    constructor(
        public printService: PrintService,
        private bsModalService: BsModalService,
        private attachmentService: AttachmentService,
        private translateService: TranslateService,
    ) {}

    ngOnInit() {
        this.fileName =
            this.attachment.name || this.translateService.instant('Attachment') + '-' + (this.fileIndex + 1);

        this.isImage = this.attachment.attachmentType === AttachmentType.image;

        this.fileType = HelperService.getFileType(HelperService.getFileExtension(this.attachment.url));

        if (this.attachment.fileType === 'file') {
            this.attachment.fileType = HelperService.getFileTypeByMimeType(this.attachment.mimeType);
        }

        if (this.isImage || HelperService.iframeSupportedFileTypesList.indexOf(this.fileType) > -1) {
            this.isPrintingAvailable =
                !this.canBeRemoved && this.attachmentService.isDownloadFileAvailable(this.attachment.url);
            this.attachment.isPrintingAvailable = this.isPrintingAvailable;
        } else {
            this.isPrintingAvailable = false;
            this.attachment.isPrintingAvailable = this.isPrintingAvailable;
        }
    }

    openDocumentViewerModal(event) {
        if (this.isPreventPreviewInModal) {
            return;
        }

        if (this.isEnablePostMessages) {
            const initialState = {
                modalId: DOCUMENT_VIEWER_MODAL_ID,
                attachments: this.attachments,
                fileIndex: this.fileIndex,
            };

            window.parent.postMessage(
                {
                    message: { initialState },
                    isFaqAttachment: true,
                },
                '*',
            );
        } else if (this.isEnablePostMessageForWidget) {
            window.parent.postMessage(
                {
                    messageType: DocumentWindowMessageType.openPreviewModal,
                    attachment: this.attachments[this.fileIndex],
                },
                '*',
            );
        } else {
            const initialState = {
                modalId: DOCUMENT_VIEWER_MODAL_ID,
                event,
                isImage: this.isImage,
                attachments: this.attachments,
                fileIndex: this.fileIndex,
                isFilePreviewInMessage: true,
                permalink: this.permalink,
                reply: this.reply,
                isForwardAllowed: this.canBeShared,
                onForwardFile: (file) => {
                    return this.forwardFile.emit(file);
                },
            };

            this.bsModalService.show(DocumentViewerModalComponent, {
                initialState,
                class: 'modal-lg modal-document-viewer',
                id: DOCUMENT_VIEWER_MODAL_ID,
            });
        }
    }

    removeCurrentAttachment(attachment) {
        const index = this.attachments.indexOf(attachment);
        this.attachments.splice(index, 1);

        if (this.isRemoveFromServer) {
            this.attachmentService.deleteFile(this.attachment.url).pipe(take(1)).subscribe();
        }
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
